import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { getConfig } from "@edx/frontend-platform";
import { FormattedMessage, injectIntl, intlShape } from "@edx/frontend-platform/i18n";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faHome } from "@fortawesome/free-solid-svg-icons";
import { useSelector } from "react-redux";
import { SelectMenu } from "@openedx/paragon";
import { Link } from "react-router-dom";
import { useModel, useModels } from "../../generic/model-store";
/** [MM-P2P] Experiment */
import { MMP2PFlyoverTrigger } from "../../experiments/mm-p2p";
import JumpNavMenuItem from "./JumpNavMenuItem";
import { ReactComponent as RightArrow } from "../../images/arrow-right.svg";
import { getDashboardPlatformURL } from "../../utils/utility";
import messages from "./messages";
import { getLocalizedFormattedNumber } from "../../utils/i18n";

function CourseBreadcrumb({
  content,
  withSeparator = false,
  courseId = null,
  unitId = null,
  intl,
  sectionId
}) {
  const defaultContent = content.filter(
    (destination) => destination.default
  )[0] || { id: courseId, label: "", sequences: [] };

  const defaultSequence = defaultContent.sequences?.filter(
    (sequence) => sequence.default
  )?.[0] || {};

  const totalSequenceUnits = defaultSequence.unitIds?.length;
  const currentSequenceUnit =
    defaultSequence.unitIds?.findIndex((id) => unitId === id) + 1;
  
  const { locale = 'en' } = useSelector((state) => state.app);

  return (
    <>
      {withSeparator && defaultContent.label && (
        <li
          className="col-auto p-0 text-truncate text-nowrap"
          role="presentation"
          aria-hidden
        >
          <span>
            /
          </span>
        </li>
      )}

      <li>
        <Link
          to={`/course/${courseId}/${sectionId}/${defaultContent.id}`}
        >
          {defaultContent.label}
        </Link>
        {!!currentSequenceUnit && (
          <>
            <span>
              /
            </span>
            <Link className="color-dark-grey"
              to={`/course/${courseId}/${sectionId}/${defaultSequence.id}`}
            >
              {defaultSequence.label}
            </Link>
            <span>
              /
            </span>
            <span className="unit-number">
              {intl.formatMessage(
                messages.courseUnitNumber,
                {
                  currentSequenceUnit: getLocalizedFormattedNumber(currentSequenceUnit, locale),
                  totalSequenceUnits: getLocalizedFormattedNumber(totalSequenceUnits, locale),
                }
              )}
            </span>
          </>
        )}
      </li>
    </>
  );
}
CourseBreadcrumb.propTypes = {
  content: PropTypes.arrayOf(
    PropTypes.shape({
      default: PropTypes.bool,
      id: PropTypes.string,
      label: PropTypes.string,
    })
  ).isRequired,
  sequenceId: PropTypes.string,
  unitId: PropTypes.string,
  withSeparator: PropTypes.bool,
  courseId: PropTypes.string,
  isStaff: PropTypes.bool,
  intl: intlShape.isRequired,
  sectionId: PropTypes.string,
};

function CourseBreadcrumbs({
  intl,
  courseId,
  sectionId = null,
  sequenceId = null,
  unitId = null,
  isStaff = null,
  /** [MM-P2P] Experiment */
  mmp2p = {},
}) {
  const course = useModel("coursewareMeta", courseId);
  const courseStatus = useSelector((state) => state.courseware.courseStatus);
  const sequenceStatus = useSelector(
    (state) => state.courseware.sequenceStatus
  );

  const allSequencesInSections = Object.fromEntries(
    useModels("sections", course.sectionIds).map((section) => [
      section.id,
      {
        default: section.id === sectionId,
        title: section.title,
        sequences: useModels("sequences", section.sequenceIds),
      },
    ])
  );

  const links = useMemo(() => {
    const chapters = [];
    if (courseStatus === "loaded" && sequenceStatus === "loaded") {
      Object.entries(allSequencesInSections).forEach(([id, section]) => {
        chapters.push({
          id,
          label: section.title,
          default: section.default,
          sequences: section.default
            ? section.sequences.map((sequence) => ({
                id: sequence.id,
                label: sequence.title,
                default: sequence.id === sequenceId,
                unitIds: sequence.unitIds,
              }))
            : [],
        });
      });
    }
    return chapters;
  }, [
    courseStatus,
    sequenceStatus,
    allSequencesInSections,
    sequenceId,
    unitId,
  ]);

  return (
    <nav aria-label="breadcrumb" className="mt-4 d-inline-block col-sm-10">
      <ol className="list-unstyled d-flex flex-wrap align-items-center adjust-padding1 m-0">
        <li className="list-unstyled col-auto m-0 p-0">
          <Link
            className="flex-shrink-0"
            to={`#`}
            onClick={() => window.location.assign(getDashboardPlatformURL())}
          >
            {intl.formatMessage(messages.homeBreadCrumb)}
          </Link>
        </li>
        <li
          className="col-auto p-0 text-truncate text-nowrap"
          role="presentation"
          aria-hidden
        >
          <span>
            /
          </span>
        </li>
        <li className="list-unstyled col-auto m-0 p-0">
          <Link
            className="flex-shrink-0"
            to={`/course/${courseId}/home`}
          >
            {course?.title}
          </Link>
        </li>
        <CourseBreadcrumb
          courseId={courseId}
          sequenceId={sequenceId}
          content={links}
          unitId={unitId}
          withSeparator
          isStaff={isStaff}
          intl={intl}
          sectionId={sectionId}
        />
        {/** [MM-P2P] Experiment */}
        {mmp2p.state && mmp2p.state.isEnabled && (
          <MMP2PFlyoverTrigger options={mmp2p} />
        )}
      </ol>
    </nav>
  );
}

export default injectIntl(CourseBreadcrumbs);

CourseBreadcrumbs.propTypes = {
  intl: intlShape.isRequired,
  courseId: PropTypes.string.isRequired,
  sectionId: PropTypes.string,
  sequenceId: PropTypes.string,
  unitId: PropTypes.string,
  isStaff: PropTypes.bool,
  /** [MM-P2P] Experiment */
  mmp2p: PropTypes.shape({
    state: PropTypes.shape({
      isEnabled: PropTypes.bool.isRequired,
    }),
  }),
};
